"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ViewToggle, { getStoredViewMode, setStoredViewMode } from "../components/ViewToggle";

export default function SessionsListPage() {
  const pageSize = 9;
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState("الكل");
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [viewMode, setViewMode] = useState("grid");
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const tabs = ["الكل", "القادمة", "المنتهية"];

  useEffect(() => {
    setViewMode(getStoredViewMode());
  }, []);

  const handleViewChange = (mode) => {
    setViewMode(mode);
    setStoredViewMode(mode);
  };

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${process.env.NEXT_PUBLIC_BASE_URL}api/sessions/v2/list?_embed`);
        setSessions(Array.isArray(response.data) ? response.data : []);
      } catch (fetchError) {
        setError(fetchError?.message || "حدث خطأ أثناء تحميل الجلسات");
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, []);

  const filteredSessions = useMemo(() => {
    const now = Date.now();
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return sessions.filter((session) => {
      const startMs = Number(session.start_time || 0) * 1000;
      const isUpcoming = startMs >= now;
      const matchesTab =
        selectedTab === "الكل" ||
        (selectedTab === "القادمة" && isUpcoming) ||
        (selectedTab === "المنتهية" && !isUpcoming);

      if (!matchesTab) return false;
      if (!normalizedSearch) return true;

      return String(session.title || "").toLowerCase().includes(normalizedSearch);
    });
  }, [sessions, selectedTab, searchTerm]);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [selectedTab, searchTerm]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (visibleCount >= filteredSessions.length) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + pageSize, filteredSessions.length));
        }
      },
      { rootMargin: "250px" }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [filteredSessions.length, visibleCount]);

  return (
    <main className="container py-5" dir="rtl">
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold mb-3">الجلسات</h1>
        <p className="lead text-muted mx-auto" style={{ maxWidth: "700px" }}>
          استعرض الجلسات القادمة والمنتهية وتابع تفاصيل كل جلسة بسهولة.
        </p>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div className="d-flex gap-2 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`btn rounded-pill px-4 py-2 ${
                selectedTab === tab ? "btn-primary" : "btn-outline-secondary"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="d-flex align-items-center gap-2">
          <ViewToggle viewMode={viewMode} onViewChange={handleViewChange} />
          <input
            type="text"
            placeholder="ابحث عن جلسة..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="form-control rounded-pill"
            style={{ maxWidth: "250px" }}
          />
        </div>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
          <p className="mt-3 text-muted">جاري تحميل الجلسات...</p>
        </div>
      )}

      {error && !loading && (
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {!loading && (
        <>
          {filteredSessions.length > 0 ? (
            viewMode === "grid" ? (
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                {filteredSessions.slice(0, visibleCount).map((session) => {
                  const startMs = Number(session.start_time || 0) * 1000;
                  const sessionDate = new Date(startMs);
                  const isUpcoming = startMs >= Date.now();
                  const statusText = isUpcoming ? "قادمة" : "منتهية";
                  const badgeClass = isUpcoming ? "bg-success" : "bg-secondary";

                  return (
                    <div key={session.id} className="col">
                      <Link href={`/sessions/${session.id}`} className="text-decoration-none text-dark d-block h-100">
                        <article className="card h-100 shadow-sm border-0 rounded-4 bg-white">
                          {session.featured_image_url ? (
                            <img src={session.featured_image_url} alt={session.title} className="card-img-top" style={{ objectFit: "cover", width: "100%", maxHeight: "220px" }} />
                          ) : (
                            <div className="card-img-top d-flex align-items-center justify-content-center bg-dark text-white" style={{ height: "220px" }}>
                              <i className="bi bi-calendar-event" style={{ fontSize: "3rem" }}></i>
                            </div>
                          )}

                          <div className="card-body d-flex flex-column">
                            <span className={`badge ${badgeClass} mb-2 align-self-start`}>{statusText}</span>
                            <h2 className="h5 fw-bold mb-2">{session.title}</h2>
                            <span className="d-block mb-2 text-muted">
                              <i className="bi bi-calendar me-1"></i>
                              {sessionDate.toLocaleDateString("ar-EG")}
                            </span>
                            <p className="text-muted mb-3 flex-grow-1" dangerouslySetInnerHTML={{ __html: session.excerpt || "" }}></p>
                            <div className="small text-muted">
                              <i className="bi bi-geo-alt me-1"></i>
                              {session.location === "online" ? "عبر الإنترنت" : "في الموقع"}
                            </div>
                          </div>
                        </article>
                      </Link>
                    </div>
                  );
                })}
              </div>
            ) : (
              <ul className="list-unstyled d-flex flex-column gap-3">
                {filteredSessions.slice(0, visibleCount).map((session) => {
                  const startMs = Number(session.start_time || 0) * 1000;
                  const sessionDate = new Date(startMs);
                  const isUpcoming = startMs >= Date.now();
                  const statusText = isUpcoming ? "قادمة" : "منتهية";
                  const badgeClass = isUpcoming ? "bg-success" : "bg-secondary";

                  return (
                    <li key={session.id}>
                      <Link href={`/sessions/${session.id}`} className="text-decoration-none text-dark">
                        <article className="card shadow-sm border-0 rounded-4 bg-white overflow-hidden">
                          <div className="row g-0">
                            <div className="col-12 col-md-4 col-lg-3">
                              {session.featured_image_url ? (
                                <img src={session.featured_image_url} alt={session.title} className="img-fluid w-100 h-100" style={{ objectFit: "cover", minHeight: "160px", maxHeight: "200px" }} />
                              ) : (
                                <div className="d-flex align-items-center justify-content-center bg-dark text-white w-100" style={{ minHeight: "160px" }}>
                                  <i className="bi bi-calendar-event" style={{ fontSize: "2.5rem" }}></i>
                                </div>
                              )}
                            </div>
                            <div className="col-12 col-md-8 col-lg-9">
                              <div className="card-body d-flex flex-column justify-content-center">
                                <span className={`badge ${badgeClass} mb-2 align-self-start`}>{statusText}</span>
                                <h2 className="h5 fw-bold mb-2">{session.title}</h2>
                                <span className="d-block mb-2 text-muted">
                                  <i className="bi bi-calendar me-1"></i>
                                  {sessionDate.toLocaleDateString("ar-EG")}
                                </span>
                                <p className="text-muted mb-2" dangerouslySetInnerHTML={{ __html: session.excerpt || "" }}></p>
                                <div className="small text-muted">
                                  <i className="bi bi-geo-alt me-1"></i>
                                  {session.location === "online" ? "عبر الإنترنت" : "في الموقع"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </article>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">لا يوجد جلسات متاحة حالياً.</p>
            </div>
          )}
        </>
      )}

      {!loading && visibleCount < filteredSessions.length && (
        <div ref={sentinelRef} className="py-4 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      )}
    </main>
  );
}
